import { usePageContext } from '../../../../../../providers/PageProvider';
import { compactSecondaryButtonClassName } from '../../../../../../styles';
import { ProposalDiffView } from '../ProposalDiffView';

export const TranscriptViewer = () => {
  const {
    session,
    previewProposal,
    activeTranscriptChunk,
    chunkRanges,
    activeTranscriptChunkIndex,
    selectTranscriptChunkIndex,
    proposalCountsByChunk,
    selectedLines,
    isBusy,
    setPreviewProposalId,
    transcriptLines,
    previewChangedLines,
    focusedLineNumber,
    setFocusedLineNumber,
    toggleLine,
    transcriptLineRefs,
  } = usePageContext();

  if (!session) {
    return null;
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-[8px] border border-[var(--color-border)] overflow-hidden shadow-sm">
      <div className="p-3 border-b border-[var(--color-border)] flex flex-wrap justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div>
            Revision {session.currentRevision} · {session.status}
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">
            {previewProposal
              ? `Diff for #${previewProposal.sequence}`
              : activeTranscriptChunk
                ? `Chunk ${activeTranscriptChunk.index} · lines ${activeTranscriptChunk.startLine}-${activeTranscriptChunk.endLine}`
                : 'Showing current draft'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="text-[var(--color-text-muted)] mr-2">{selectedLines.length} selected</div>
          {chunkRanges.length ? (
            <>
              <button
                className={compactSecondaryButtonClassName}
                disabled={isBusy || activeTranscriptChunkIndex === 0}
                onClick={() => selectTranscriptChunkIndex(activeTranscriptChunkIndex - 1)}
              >
                Previous Chunk
              </button>
              <select
                className={compactSecondaryButtonClassName}
                value={activeTranscriptChunkIndex}
                onChange={(event) => selectTranscriptChunkIndex(Number(event.target.value))}
              >
                {chunkRanges.map((chunk, index) => (
                  <option key={chunk.index} value={index}>
                    Chunk {chunk.index} · L{chunk.startLine}-{chunk.endLine} ·{' '}
                    {proposalCountsByChunk.get(chunk.index)?.total ?? 0} edits
                  </option>
                ))}
              </select>
              <button
                className={compactSecondaryButtonClassName}
                disabled={isBusy || activeTranscriptChunkIndex >= chunkRanges.length - 1}
                onClick={() => selectTranscriptChunkIndex(activeTranscriptChunkIndex + 1)}
              >
                Next Chunk
              </button>
            </>
          ) : null}
          <button
            className={compactSecondaryButtonClassName}
            disabled={isBusy || !previewProposal}
            onClick={() => setPreviewProposalId(null)}
          >
            Hide Diff
          </button>
        </div>
      </div>
      {previewProposal ? (
        <ProposalDiffView draftTranscript={session.draftTranscript} proposal={previewProposal} />
      ) : (
        <div className="max-h-[720px] overflow-auto font-mono text-xs">
          {transcriptLines.map((line, index) => {
            const lineNumber = index + 1;
            if (
              activeTranscriptChunk &&
              (lineNumber < activeTranscriptChunk.startLine || lineNumber > activeTranscriptChunk.endLine)
            ) {
              return null;
            }
            const selected = selectedLines.includes(lineNumber);
            const previewChanged = previewChangedLines.has(lineNumber);
            const focused = focusedLineNumber === lineNumber;
            const activeChunkLine =
              !!activeTranscriptChunk &&
              lineNumber >= activeTranscriptChunk.startLine &&
              lineNumber <= activeTranscriptChunk.endLine;

            return (
              <button
                className={`w-full grid grid-cols-[40px_minmax(0,1fr)] md:grid-cols-[64px_minmax(0,1fr)] text-left gap-3 px-3 py-1 border-b border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-tint)] ${focused ? 'bg-[var(--color-info-bg)] ring-1 ring-inset ring-[var(--color-fixed-accent)]' : selected || previewChanged || activeChunkLine ? 'bg-[var(--color-tint)]' : 'bg-[var(--color-surface)]'}`}
                key={lineNumber}
                onClick={() => {
                  setFocusedLineNumber(null);
                  toggleLine(lineNumber);
                }}
                ref={(element) => {
                  transcriptLineRefs.current[lineNumber] = element;
                }}
              >
                <span className="text-[var(--color-text-muted)]">{lineNumber}</span>
                <span className="whitespace-pre-wrap">{line || ' '}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
